import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "../../utils";
import { 
  EmailTemplate, 
  EmailTemplateFolder, 
  EmailTemplateShare 
} from "./types";

const emailTemplatesQueryKey = "email-templates";

// Folders hooks (RLS)
export function useTemplateFolders() {
  return useQuery<EmailTemplateFolder[]>({
    queryKey: ["email-template-folders"],
    queryFn: async () => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("email_template_folders")
        .select("*")
        .order("name", { ascending: true });
      if (error) throw new Error(error.message);
      return data || [];
    },
  });
}

export function useCreateFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, parent_id }: { name: string; parent_id?: string | null }) => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("email_template_folders")
        .insert([{ name, parent_id: parent_id || null }])
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data as EmailTemplateFolder;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["email-template-folders"] }),
  });
}

export function useRenameFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("email_template_folders")
        .update({ name })
        .eq("id", id)
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data as EmailTemplateFolder;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["email-template-folders"] }),
  });
}

export function useDeleteFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const supabase = getSupabase();
      const { error } = await supabase.from("email_template_folders").delete().eq("id", id);
      if (error) throw new Error(error.message);
      return { id };
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["email-template-folders"] }),
  });
}

export function useMoveTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      templateId,
      folderId,
    }: {
      templateId: string;
      folderId: string | null;
    }) => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("email_templates")
        .update({ folder_id: folderId })
        .eq("id", templateId)
        .select("*, folder:folder_id(id,name,parent_id)")
        .single();
      if (error) throw new Error(error.message);
      return data as EmailTemplate;
    },
    onSuccess: (data) => {
      const folderName = data.folder?.name || "No folder";
      toast.success(`Template moved to "${folderName}" successfully`);
      void queryClient.invalidateQueries({ queryKey: [emailTemplatesQueryKey] });
    },
    onError: (error) => {
      toast.error(`Failed to move template: ${error.message}`);
    },
  });
}
